import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/rootReducer";
import { Link, Skeleton, Typography, SxProps, Theme } from "@mui/material";
import { fetchDocumentsStart } from "../features/documents/store/documentsSlice";
import {
  selectDocumentById,
  selectDocumentsLoading,
} from "../features/documents/store/documentsSelectors";
import { StratifyDocument } from "@stratify/shared";

interface DocumentLinkProps {
  documentId: string;
  variant?: "inherit" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "subtitle1" | "subtitle2" | "body1" | "body2" | "caption" | "button" | "overline";
  sx?: SxProps<Theme>;
}

export const DocumentLink: React.FC<DocumentLinkProps> = ({ 
  documentId, 
  variant = "body2", 
  sx 
}) => {
  const document = useSelector((state: RootState) =>
    selectDocumentById(state, documentId)
  ) as StratifyDocument | undefined;
  const isLoading = useSelector(selectDocumentsLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    // Fetch document if it's not in the store and not already loading
    if (!document && !isLoading) {
      dispatch(fetchDocumentsStart());
    }
  }, [document, isLoading, dispatch, documentId]);

  if (isLoading && !document) {
    return <Skeleton variant="text" width="60%" />;
  }

  if (!document) {
    return (
      <Typography variant="caption" color="text.secondary">
        Document not available.
      </Typography>
    );
  }

  const documentPath = document.propertyId
    ? `/properties/${document.propertyId}/documents/${document.id}`
    : `/documents/${document.id}`;

  return (
    <Link
      href={documentPath}
      target="_blank"
      rel="noopener noreferrer"
      variant={variant}
      sx={{
        display: "block",
        color: "primary.main",
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline",
        },
        ...sx,
      }}
    >
      {document.filename}
    </Link>
  );
};
